import React, { ReactElement, ReactNode, useCallback } from 'react'
import { VpUser } from '../../../app/types'
import { FormSection } from '../../../common/components/SectionedForm'
import { api } from '../../../common/api'
import { useSelector } from 'react-redux'
import { customerOriginIdSelector } from '../../../authentication/redux'
import { useTranslation } from 'react-i18next'
import PaperStack from '../../../common/form/PaperStack'
import FormGroup from '../../../common/form/FormGroup'
import { LabelValues, LabelValueType } from '../../../common/components/LabelValues'
import PersonIcon from '@mui/icons-material/Person'
import { useFormSubmissionButtons } from '../../../common/form/useFormSubmissmionButtons'
import useAdminVpUserForm from '../useAdminVpUserForm'
import FormContainer from '../../../common/form/FormContainer'

export const useVpUserGeneralSection = (userId: string, isFetching: boolean, error: any, refetch: () => void, data?: VpUser): FormSection<VpUser> => {
    const { t } = useTranslation()
    const customerOriginId = useSelector(customerOriginIdSelector)
    const { buildFormButtons } = useFormSubmissionButtons(false)

    const onSubmit = useCallback((onSucceeded: () => void) =>
            async (formData: any) => {
                if (!customerOriginId) return
                await api.put(`/api/enterprise/${customerOriginId}/vpUsers/${userId}`, formData)
                await refetch()
                onSucceeded()
            },
        [customerOriginId, userId, refetch],
    )

    const infoLabelValues = useCallback(
        (user: VpUser): LabelValueType[] => [
            { label: t('users.edit.lastName'), value: <>{user.lastName}</> },
            { label: t('users.edit.firstName'), value: <>{user.firstName}</> },
            { label: t('users.edit.email'), value: <>{user.email}</> },
        ],
        [t],
    )

    const renderDetails = useCallback(
        (user: VpUser): ReactNode => {
            return (
                <PaperStack>
                    <FormGroup key={1} label={t('general.general')} fullWidth={true}>
                        <LabelValues labelValues={infoLabelValues(user)} />
                    </FormGroup>
                    {null}
                </PaperStack>
            )
        },
        [infoLabelValues, t],
    )

    const renderForm = useCallback((user: VpUser, cancelEditButton: ReactElement, onSucceeded: () => void): ReactNode => {
        return <VpUserEditForm user={user} onSubmit={onSubmit(onSucceeded)} buildFormButtons={buildFormButtons(cancelEditButton)} />
    }, [onSubmit, buildFormButtons])

    return {
        title: t('general.general'),
        data: data,
        isFetching: isFetching,
        error: error,
        refetch: refetch,
        renderDetails: renderDetails,
        renderForm: renderForm,
        enabled: () => true,
        icon: () => <PersonIcon />,
    }
}

// Inline edit form component
const VpUserEditForm = ({ user, onSubmit, buildFormButtons }: {
    user: VpUser,
    onSubmit: (formData: any) => Promise<void>,
    buildFormButtons: (isSubmitting: boolean) => ReactNode
}) => {
    const { t } = useTranslation()
    const { handleSubmit, form: Form, reset, setValue } = useAdminVpUserForm({ entry: user, onSubmit })

    React.useEffect(() => {
        reset()
        setValue('lastName', user.lastName)
        setValue('firstName', user.firstName)
        setValue('email', user.email)
    }, [user, reset, setValue])

    return (
        <FormContainer onSubmit={handleSubmit}>
            <FormGroup label={t('general.general')} fullWidth={true}>
                <Form />
            </FormGroup>
            {buildFormButtons(false)}
        </FormContainer>
    )
}


